<template>
  <div>
    <TopBar />

    <GridContainer>
      <GridRow v-if="!$auth.loading">
        <SideBar v-if="!$route.meta.noProjectOk" />

        <GridCol
          :sm="$route.meta.noProjectOk ? 12 : 9"
          :md="$route.meta.noProjectOk ? 12 : 10"
          class="main"
        >
          <!-- block main_content -->
          <div class="page-header">
            <div class="pull-right apim-printonly">
              <a href="http://apimetrics.io/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/img/apimetrics-context-logo-color-transparent.png"
                  alt="APImetrics by API Context"
                  title="APImetrics by API Context"
                  style="height: 48px"
                />
              </a>
            </div>
            <h1 v-tooltip="pageTitle">
              {{ pageTitle }}
            </h1>
            <p v-if="pageDesc" class="info-label">
              {{ pageDesc }}
            </p>
          </div>

          <!-- block pre_content -->
          <Breadcrumbs class="apim-noprint" />
          <!-- endblock pre_content -->

          <ModalDialog />

          <div v-if="invites.length">
            <div v-for="invite in invites" :key="invite.id" class="alert alert-info">
              <b>Invite</b>: You have a pending invite for
              <span v-if="invite.project_id">
                Project <b>{{ invite.name }}</b
                >.
              </span>
              <span v-else>
                Organization <b>{{ invite.name }}</b
                >. Note that changing Organizations may mean you lose access to existing projects.
              </span>
              {{ ' ' }}
              <a @click.prevent="acceptInvite(invite)" @keyup.enter="acceptInvite(invite)">
                <i class="glyphicon glyphicon-check" /> Accept
              </a>
              <a @click.prevent="rejectInvite(invite)" @keyup.enter="rejectInvite(invite)">
                <i class="glyphicon glyphicon-trash" /> Reject
              </a>
            </div>
          </div>
          <div v-if="apiErrors">
            <div
              v-for="(error, name, idx) in apiErrors"
              :key="'err-' + idx + '-' + name"
              class="alert alert-danger"
            >
              <b>Error {{ error.status || name }}</b
              >: API returned
              {{ error.body ? error.body.error_msg || error.message : error.message }}.
              <a
                class="pull-right"
                @click.prevent="clearAPIErrors()"
                @keyup.enter="clearAPIErrors()"
              >
                <i class="glyphicon glyphicon-trash" /><span class="sr-only">Dismiss</span>
              </a>
            </div>
          </div>
          <!-- block content -->
          <div v-if="!$auth.loading">
            <div
              v-if="
                !hideAlert('verify') &&
                $auth.user &&
                !$auth.user.email_verified &&
                !verifySent &&
                !emailVerifyNeeded
              "
              class="alert alert-warning"
            >
              <b>Warning:</b> Please
              <a @click.prevent="verify" @keyup.enter="verify">verify your email address</a>
              to keep your account secure.
              <a
                class="pull-right"
                @click.prevent="clearAlert('verify')"
                @keyup.enter="clearAlert('verify')"
              >
                <i class="glyphicon glyphicon-trash" /><span class="sr-only">Dismiss</span>
              </a>
            </div>
            <div
              v-if="
                !hideAlert('verify') &&
                $auth.user &&
                !$auth.user.email_verified &&
                verifySent &&
                !emailVerifyNeeded
              "
              class="alert alert-warning"
            >
              <b>Warning:</b> Please check your inbox to verify your email address associated with
              this account. <b>Problems?</b> Please
              <a href="http://apimetrics.io/contact/" target="_blank" rel="noopener noreferrer"
                >Contact Us</a
              >
              for support.
              <a
                class="pull-right"
                @click.prevent="clearAlert('verify')"
                @keyup.enter="clearAlert('verify')"
              >
                <i class="glyphicon glyphicon-trash" /><span class="sr-only">Dismiss</span>
              </a>
            </div>
            <div
              v-if="
                !hideAlert('apis') &&
                $auth.user &&
                !$auth.user.email_verified &&
                !verifySent &&
                emailVerifyNeeded
              "
              class="alert alert-danger"
            >
              <b>Where are my API Calls?</b>
              <br />
              To ensure security, please
              <a @click.prevent="verify" @keyup.enter="verify">verify your email address</a>
              to regain access to your existing project or view invites.
              <a
                class="pull-right"
                @click.prevent="clearAlert('apis')"
                @keyup.enter="clearAlert('apis')"
              >
                <i class="glyphicon glyphicon-trash" /><span class="sr-only">Dismiss</span>
              </a>
            </div>
            <div
              v-if="
                !hideAlert('apis') &&
                $auth.user &&
                !$auth.user.email_verified &&
                verifySent &&
                emailVerifyNeeded
              "
              class="alert alert-danger"
            >
              <b>Where are my API Calls?</b>
              <br />
              Please check your inbox to verify your email address to regain access to your existing
              project.<br />
              <b>Problems?</b> Please
              <a href="http://apimetrics.io/contact/" target="_blank" rel="noopener noreferrer"
                >Contact Us</a
              >
              for support.
              <a
                class="pull-right"
                @click.prevent="clearAlert('apis')"
                @keyup.enter="clearAlert('apis')"
              >
                <i class="glyphicon glyphicon-trash" /><span class="sr-only">Dismiss</span>
              </a>
            </div>
            <div
              v-if="
                $route.meta.siteAdminOnly &&
                (!$auth.isAuthenticated || (!isSiteViewer && !hasQuotaViewPermission))
              "
            >
              <GridRow>
                <GridCol :md="6">
                  <div style="width: 100%; height: 0; padding-bottom: 83%; position: relative">
                    <iframe
                      src="https://giphy.com/embed/wSSooF0fJM97W"
                      width="100%"
                      height="100%"
                      style="position: absolute"
                      frameBorder="0"
                      class="giphy-embed"
                      allowFullScreen
                      title="Dennis Nedry says you don't have access"
                    />
                  </div>
                  <p>
                    <a href="https://giphy.com/gifs/hacker-dennis-timbenj-wSSooF0fJM97W"
                      >via GIPHY</a
                    >
                  </p>
                </GridCol>
              </GridRow>
            </div>
            <div
              v-else-if="
                (!nextProjectId && loaded && projectId) ||
                ($auth.isAuthenticated && $route.meta.noProjectOk) ||
                $route.meta.noAuthOk
              "
            >
              <div
                v-if="
                  !$route.meta.noProjectOk &&
                  projectId &&
                  project &&
                  project.system_tags &&
                  project.system_tags.includes('out_of_quota')
                "
                class="alert alert-warning"
              >
                <b>Out of quota</b>: Project <em>{{ project.name }}</em> does not have a
                subscription or has used up all it's quota.
                <router-link :to="{ name: 'billing-info', params: { subId: 'current' } }">
                  <i class="glyphicon glyphicon-book" /> Subscribe now!
                </router-link>
              </div>
              <router-view />
            </div>
            <div v-else-if="nextProjectId">
              <div class="alert alert-info">
                Please wait as we load the project <LoadingAnim no-text tag="span" />
              </div>
            </div>
            <div v-else>
              <div v-if="!$auth.isAuthenticated && !$route.meta.noAuthOk">
                <div class="alert alert-danger">
                  You need to <a @click="doLogin" @keyup.enter="doLogin">log in</a> to access this
                  page.
                </div>
                <button class="btn btn-primary btn-large" type="button" @click="doLogin">
                  Login
                </button>
              </div>
              <div v-else-if="!loading && loaded && !projectId" class="alert alert-danger">
                You must have an active project to view this page.
              </div>
              <LoadingAnim v-else />
            </div>
          </div>
          <LoadingAnim v-else />
          <!-- endblock content -->
          <FooterBar />
        </GridCol>
      </GridRow>
    </GridContainer>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import { get, call } from 'vuex-pathify';

import TopBar from '../components/TopBar.vue';
import SideBar from '../components/SideBar.vue';
import FooterBar from '../components/Footer.vue';
import Breadcrumbs from '../components/Breadcrumbs.vue';
import ModalDialog from '../components/ModalDialog.vue';

const AUDIENCE = process.env.VUE_APP_AUTH0_AUDIENCE;

export default {
  name: 'APImetrics',
  components: {
    Breadcrumbs,
    TopBar,
    SideBar,
    FooterBar,
    ModalDialog,
  },
  data() {
    return {
      verifySent: false,
      cookieMirror: {},
    };
  },
  computed: {
    pageTitle() {
      return this.routePageTitle?.title || this.$route.meta.title || process.env.VUE_APP_TITLE;
    },
    pageDesc() {
      return this.$route.meta.description;
    },
    routeName() {
      return this.$route.name;
    },
    expiresInPast() {
      if (this.quotaInfo) {
        return moment.utc(this.quotaInfo.trial_expiry_date) < moment.utc();
      }
      return false;
    },
    quotaTrialEnds() {
      return moment
        .duration(moment.utc(this.quotaInfo.trial_expiry_date).diff(moment.utc()))
        .humanize(true);
    },
    userRoles() {
      if (!this.$auth.loading && this.$auth.isAuthenticated && this.$auth.user) {
        return this.$auth.user[`${AUDIENCE}/permissions`];
      }
      return [];
    },
    hasQuotaViewPermission() {
      return this.userRoles.includes('quota_viewer');
    },
    routePageTitle: get('account2/pageTitles[:routeName]'),
    loading: get('account2/loading'),
    loaded: get('account2/loaded'),
    invites: get('account2/invites'),
    projectId: get('account2/projectId'),
    nextProjectId: get('account2/nextProjectId'),
    projects: get('account2/projects'),
    emailVerifyNeeded: get('account2/verifyNeeded'),
    project: get('account2/projects[:projectId].project'),
    projectName: get('account2/projects[:projectId].project.name'),
    quotaInfo: get('account2/quotaInfo'),
    isSiteViewer: get('account2/isSiteViewer'),
    apiErrors: get('apimAPI@apiErrors'),
    latestError: get('apimAPI@latestError'),
  },
  watch: {
    latestError(val) {
      if (val) {
        const { status } = val;
        // Check for logged out
        if (status && status === 403) {
          this.$auth.getTokenSilently();
        }
      }
    },
    pageTitle(val) {
      document.title = val;
    },
    $route() {
      // window.Intercom('update');
    },
    '$auth.loading': function _isAuth(loading) {
      if (!loading) {
        this.fetchData();
      }
    },
    '$auth.isAuthenticated': function _isAuth(isAuthenticated) {
      if (isAuthenticated) {
        this.fetchData();
        this.$nextTick(async () => {
          this.updateUserInfoBasic();
        });
      }
    },
  },
  async mounted() {
    this.fetchData();
    // Setup jira chat
    const chatWidget = document.querySelector('chat-widget') || {};
    const that = this;
    chatWidget.onload = async function _chatOnLoad() {
      if (!that.$auth.loading && that.$auth.isAuthenticated) {
        try {
          const token = that.$fetch.jiraChatLogin();
          await chatWidget.loginWithToken(token);
        } catch (e) {
          console.error('Failed to login to chat widget', e);
        }
      }
    };
  },
  methods: {
    async fetchData() {
      await this.clearAPIErrors();
      if (!this.$auth.loading && this.$auth.isAuthenticated && this.$auth.user) {
        await this.fetchAccountProjects(this.$route.meta);
      }
    },
    async verify() {
      this.$fetcher.verifyEmail();
      this.verifySent = true;
    },
    async acceptInvite(invite) {
      await this.$fetcher.acceptInvite(invite);
      window.location.reload();
    },
    async rejectInvite(invite) {
      await this.$fetcher.rejectInvite(invite);
      return this.fetchAccountProjects(this.$route.meta);
    },
    doLogin() {
      this.$cookies.set('continue-url', { path: this.$route.path }, '1h');
      this.$auth.loginWithRedirect();
    },
    hideAlert(name) {
      const cookieName = `alert-${name}`;
      if (this.$auth.isAuthenticated && this.$auth.user?.sub && this.$cookies.isKey(cookieName)) {
        const cookie = this.cookieMirror[cookieName] || this.$cookies.get(cookieName);
        return cookie === this.$auth.user.sub;
      }
      return false;
    },
    clearAlert(name) {
      if (this.$auth.isAuthenticated && this.$auth.user?.sub) {
        const cookieName = `alert-${name}`;
        this.$cookies.set(cookieName, this.$auth.user.sub, '7d');
        this.$set(this.cookieMirror, cookieName, this.$auth.user.sub);
      }
    },
    fetchAccountProjects: call('account2/fetchAccountProjects'),
    updateUserInfoBasic: call('account2/updateUserInfoBasic'),
    ...call('apimAPI', ['clearAPIErrors']),
  },
};
</script>

<style>
@media (min-width: 768px) {
  .dl-horizontal dt {
    width: 240px;
  }

  .dl-horizontal dd {
    margin-left: 252px;
  }
}

.select2-container--bootstrap .select2-selection {
  font-family: 'Open Sans', 'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 16px;
  color: var(--offwhite) !important;
}
.select2-container--bootstrap .select2-results {
  font-family: 'Open Sans', 'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 16px;
  color: var(--offwhite);
}
.new-select .select2-container .select2-selection--single {
  height: 48px !important;
}

.new-select .select2-container--bootstrap .select2-container-open .select2-dropdown--below {
  font-family: 'Open Sans', 'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 16px;
  color: var(--offwhite);
}
.new-select .select2-container .select2-selection--single .select2-selection__rendered {
  padding-top: 8px;
}
.new-select .select2-container--bootstrap .select2-selection--single .select2-selection__arrow b {
  border-width: 6px 6px 0 6px;
}
.new-select
  .select2-container--bootstrap.select2-container--open
  .select2-selection
  .select2-selection__arrow
  b {
  border-width: 0 6px 6px 6px;
}

body {
  padding-top: 0px !important;
}

td > .checkbox input[type='checkbox'],
td > .checkbox-inline input[type='checkbox'],
td > .radio input[type='radio'],
td > .radio-inline input[type='radio'] {
  height: 20px;
  /* line-height */
  width: 14px;
  /* font-size */
}

.alert {
  background: var(--dark-secondary) !important;
  color: var(--white);
}

.alert a {
  color: var(--warn) !important;
}
</style>
